import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Spinner, Table, Form } from "react-bootstrap";
import {
  Upload, Download, FileText, FileJson, CheckCircle, XCircle,
  Database, FileSpreadsheet,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { PageHeader } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./AssessmentAnalytics.scss";

const SAMPLE_CSV = `title,mode,timeLimit,maxAttempts,passingPercentage,instructions
"Sample Quiz 1",practice,30,3,60,"Answer all questions carefully"
"Final Exam",exam,90,1,40,"No negative marking. Good luck!"`;

const SAMPLE_JSON = JSON.stringify({
  assessments: [
    {
      title: "Sample Quiz 1",
      mode: "practice",
      timeLimit: 30,
      maxAttempts: 3,
      passingPercentage: 60,
      instructions: "Answer all questions carefully",
    },
  ],
}, null, 2);

const BulkOperationsPage = () => {
  const fileRef = useRef(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // ── Export state ──
  const [exporting, setExporting] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [exportAssessmentId, setExportAssessmentId] = useState("");

  // ── Import state ──
  const [file, setFile] = useState(null);
  const [parsedAssessments, setParsedAssessments] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    apiClient.get("/assessments?limit=200")
      .then(({ data }) => setAssessments(data.assessments || []))
      .catch(() => {});
  }, []);

  // ── Export handlers ──────────────────────────────────────────────────
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = { format };
      if (exportAssessmentId) params.assessmentId = exportAssessmentId;

      if (format === "csv") {
        const { data } = await apiClient.get("/assessment-analytics/export", {
          params,
          responseType: "blob",
        });
        const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "assessment_results.csv";
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const { data } = await apiClient.get("/assessment-analytics/export", { params });
        const blob = new Blob([JSON.stringify(data.results, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "assessment_results.json";
        a.click();
        URL.revokeObjectURL(url);
      }
      setToast({ show: true, message: "Export downloaded successfully", type: "success" });
    } catch (err) {
      const msg = err?.response?.data?.message || "Export failed";
      setToast({ show: true, message: msg, type: "error" });
    }
    setExporting(false);
  };

  // ── CSV parser ───────────────────────────────────────────────────────
  const parseCSV = useCallback((text) => {
    const lines = text.split("\n").filter((l) => l.trim());
    if (lines.length < 2) {
      setParseErrors(["CSV must have a header row and at least one data row"]);
      return [];
    }

    const headers = lines[0].split(",").map((h) => h.trim().replaceAll(/^"|"$/g, ""));
    const items = [];
    const errors = [];

    for (let i = 1; i < lines.length; i++) {
      try {
        const values = [];
        let current = "";
        let inQuotes = false;
        for (const char of lines[i]) {
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === "," && !inQuotes) {
            values.push(current.trim());
            current = "";
          } else {
            current += char;
          }
        }
        values.push(current.trim());

        const row = {};
        headers.forEach((h, idx) => {
          row[h] = values[idx] || "";
        });

        if (!row.title) {
          errors.push(`Row ${i + 1}: Missing title`);
          continue;
        }

        items.push({
          title: row.title,
          mode: row.mode || "practice",
          timeLimit: Number.parseInt(row.timeLimit, 10) || 0,
          maxAttempts: Number.parseInt(row.maxAttempts, 10) || 0,
          passingPercentage: Number.parseFloat(row.passingPercentage) || 0,
          instructions: row.instructions || "",
          negativeMarking: row.negativeMarking === "true",
          shuffleQuestions: row.shuffleQuestions === "true",
        });
      } catch {
        errors.push(`Row ${i + 1}: Parse error`);
      }
    }

    setParseErrors(errors);
    return items;
  }, []);

  // ── File handler ─────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    setFile(selected);
    setImportResult(null);
    setParseErrors([]);

    const text = await selected.text();
    if (selected.name.endsWith(".json")) {
      try {
        const data = JSON.parse(text);
        const items = Array.isArray(data) ? data : data.assessments || [];
        setParsedAssessments(items);
        if (items.length === 0) setParseErrors(["No assessments found in JSON file"]);
      } catch {
        setParseErrors(["Invalid JSON file"]);
        setParsedAssessments([]);
      }
    } else {
      const items = parseCSV(text);
      setParsedAssessments(items);
    }
  };

  // ── Upload handler ───────────────────────────────────────────────────
  const handleUpload = async () => {
    if (parsedAssessments.length === 0) return;
    setUploading(true);

    try {
      const { data } = await apiClient.post("/assessment-analytics/bulk-import", {
        assessments: parsedAssessments,
      });
      setImportResult({ type: "success", message: data.message, created: data.created, errors: data.errors });
      setToast({ show: true, message: data.message, type: "success" });
    } catch (err) {
      const msg = err.response?.data?.message || "Import failed";
      setImportResult({ type: "error", message: msg });
      setToast({ show: true, message: msg, type: "error" });
    }
    setUploading(false);
  };

  // ── Download sample ──────────────────────────────────────────────────
  const downloadSample = (format) => {
    const content = format === "csv" ? SAMPLE_CSV : SAMPLE_JSON;
    const type = format === "csv" ? "text/csv" : "application/json";
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `assessment_template.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bulk-operations-page">
      <PageHeader
        backButton
        icon={Database}
        title="Bulk Operations"
        description="Export results and import assessments in bulk"
      />

      {/* ── Export Section ─────────────────────────────────────────── */}
      <div className="bulk-section">
        <div className="bulk-section__title">
          <Download size={18} /> Export Assessment Results
        </div>
        <div className="bulk-section__desc">
          Download student attempt results as CSV or JSON. Optionally filter by assessment.
        </div>

        <div className="d-flex gap-3 align-items-end flex-wrap mb-3">
          <div style={{ minWidth: 220 }}>
            <Form.Label className="small text-muted mb-1">Assessment (optional)</Form.Label>
            <Form.Select size="sm" value={exportAssessmentId} onChange={(e) => setExportAssessmentId(e.target.value)}>
              <option value="">All Assessments</option>
              {assessments.map((a) => (
                <option key={a._id} value={a._id}>{a.title}</option>
              ))}
            </Form.Select>
          </div>
        </div>

        <div className="export-options">
          <button type="button" className="export-card" onClick={() => !exporting && handleExport("csv")}>
            <div className="export-card__icon export-card__icon--csv">
              <FileSpreadsheet size={18} />
            </div>
            <div className="export-card__label">Export as CSV</div>
            <div className="export-card__hint">Spreadsheet-compatible format (Excel, Google Sheets)</div>
            {exporting && <Spinner size="sm" animation="border" />}
          </button>

          <button type="button" className="export-card" onClick={() => !exporting && handleExport("json")}>
            <div className="export-card__icon export-card__icon--json">
              <FileJson size={18} />
            </div>
            <div className="export-card__label">Export as JSON</div>
            <div className="export-card__hint">Structured data format for programmatic use</div>
            {exporting && <Spinner size="sm" animation="border" />}
          </button>
        </div>
      </div>

      {/* ── Import Section ─────────────────────────────────────────── */}
      <div className="bulk-section">
        <div className="bulk-section__title">
          <Upload size={18} /> Bulk Import Assessments
        </div>
        <div className="bulk-section__desc">
          Upload a CSV or JSON file to create multiple assessment drafts at once (max 50).
        </div>

        <div className="d-flex gap-2 mb-3">
          <Button variant="outline-secondary" size="sm" onClick={() => downloadSample("csv")}>
            <Download size={14} className="me-1" /> Sample CSV
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={() => downloadSample("json")}>
            <Download size={14} className="me-1" /> Sample JSON
          </Button>
        </div>

        <button
          type="button"
          className="upload-dropzone"
          onClick={() => fileRef.current?.click()}
        >
          <div className="upload-dropzone__icon">
            <FileText size={40} />
          </div>
          <div className="upload-dropzone__text">
            {file ? file.name : "Click to select a CSV or JSON file"}
          </div>
          <div className="upload-dropzone__hint">
            Supported: .csv, .json — Max 50 assessments per upload
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.json"
            onChange={handleFileChange}
            className="d-none"
          />
        </button>

        {/* Parse Errors */}
        {parseErrors.length > 0 && (
          <div className="upload-errors mt-3">
            {parseErrors.map((err, i) => (
              <div className="error-item" key={`err-${err.slice(0, 20)}`}>
                <XCircle size={14} className="me-1" /> {err}
              </div>
            ))}
          </div>
        )}

        {/* Preview Table */}
        {parsedAssessments.length > 0 && !importResult && (
          <>
            <div className="mt-3 mb-2 d-flex justify-content-between align-items-center">
              <span className="fw-semibold">{parsedAssessments.length} assessments parsed</span>
              <Button variant="primary" onClick={handleUpload} disabled={uploading}>
                {uploading ? <Spinner size="sm" animation="border" /> : "Import All as Drafts"}
              </Button>
            </div>
            <div className="preview-table">
              <Table size="sm" bordered hover>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Title</th>
                    <th>Mode</th>
                    <th>Time Limit</th>
                    <th>Max Attempts</th>
                    <th>Passing %</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedAssessments.map((a, i) => (
                    <tr key={a.title || i}>
                      <td>{i + 1}</td>
                      <td>{a.title}</td>
                      <td>{a.mode || "practice"}</td>
                      <td>{a.timeLimit || "—"} min</td>
                      <td>{a.maxAttempts || "Unlimited"}</td>
                      <td>{a.passingPercentage || 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {/* Import Result */}
        {importResult && (
          <div className={`import-result import-result--${importResult.type} mt-3`}>
            {importResult.type === "success" ? (
              <CheckCircle size={18} />
            ) : (
              <XCircle size={18} />
            )}
            <span>{importResult.message}</span>
          </div>
        )}
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default BulkOperationsPage;
